import {
  adHocSearchActions,
  mentionSearchActions,
  selectAdHocSearch,
} from '@repo/redux/actions/search';
import { SearchState, SearchType } from '@repo/utils/types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

interface SearchConfig {
  defaultTerm?: string;
  resultCount?: number;
  searchAttributes?: Array<string>;
  type?: SearchType;
  useMainSearchSorting?: boolean;
}

const actionsBySearchType = {
  adHocSearch: adHocSearchActions,
  mentionSearch: mentionSearchActions,
};
// Returns a setter and a searchState interface. The setter will synchronously update
// the search term in searchState and result in debounced queries that will update the
// results stored in searchState. defaultTerm is the query term to use when the setter
// has no value applied (input value == '')
export const useContactSearch = ({
  defaultTerm = '',
  resultCount = 5,
  searchAttributes = [],
  type = 'adHocSearch',
  useMainSearchSorting = false,
}: SearchConfig): [(term: string) => void, SearchState] => {
  const dispatch = useDispatch();
  const searchState = useSelector(selectAdHocSearch(type));
  const { setTerm, clear } = actionsBySearchType[type];

  const setSearchTerm = (term: string) => {
    const nextSearchTerm = term != '' ? term : defaultTerm;
    dispatch(setTerm(nextSearchTerm, resultCount, searchAttributes, useMainSearchSorting));
  };

  useEffect(() => {
    setSearchTerm(defaultTerm);
    return () => {
      dispatch(clear());
    };
  }, []);

  return [setSearchTerm, searchState];
};

export const useFullContactSearch = ({ term = 'all' }: { term?: string }): { loading: boolean } => {
  const dispatch = useDispatch();
  const searchState = useSelector(selectAdHocSearch('adHocSearch'));
  const { clear, setTerm, fetchMore } = actionsBySearchType['adHocSearch'];

  useEffect(() => {
    dispatch(setTerm(term, 1000, [], false));

    return () => {
      dispatch(clear());
    };
  }, []);

  useEffect(() => {
    if (searchState.results.canLoadMore && searchState.results.updated) {
      dispatch(fetchMore(1000));
    }
  }, [searchState.results.updated]);

  return { loading: searchState.results.canLoadMore };
};
